// The ONE answer to "across this auction's rings, do buyers pay different
// rates — and which rings do we list?" (BUG-1347).
//
// Two surfaces disclose an auction's ring-by-ring buyer's premium: the bidder
// auction page (a pill or a table) and the printed Buyers Guide (one terms
// block or a line per ring). Before this module they each decided it, and they
// had already drifted — the website compared what a buyer SEES, the guide
// compared `JSON.stringify` of the stored schedule, so the same auction could
// print per-ring terms on paper and a single pill on screen.
//
// Both also mapped EVERY ring on the auction, with no gate on whether the ring
// holds a lot a buyer can see. That is the reported defect: a configured but
// empty ring quoted a rate no buyer of that sale could be billed, and — worse —
// its rate voted in the uniform-vs-varies decision, so an empty ring could flip
// an otherwise-uniform auction into a "varies by ring" disclosure over a
// difference nobody could encounter. That is the exact class of misleading
// disclosure BL-1141 removed, reintroduced through the empty-ring path.
//
// An IsomorphicService (ADR 010): pure, read-only, generic on input shape.
// Callers pass rings they have already resolved through the cascade
// (`resolveBuyersPremium`, ADR 032 §1) and get back a decision; this module
// never resolves, fetches, or formats for display.

import { buyersPremiumTermsKey } from './format';
import type { BuyersPremiumScheduleInput } from './format';

/**
 * One ring, as this decision needs to see it.
 *
 * Generic on `TRing` per ADR 010 §2 so each surface passes its own row/label
 * shape and gets it back untouched — the bidder threads `{ringId, ringNumber}`,
 * the printed guide threads `{ringId, ringLabel, terms}`. This module never
 * reads inside `ring`.
 */
export interface DisclosableRing<TRing> {
  /** The caller's own ring shape, returned verbatim in the `varies` arm. */
  ring: TRing;
  /**
   * The rate this ring's lots actually pay — ALREADY resolved down the cascade
   * (`ring → auction → house`). Not the raw stored rung: a ring that overrides
   * nothing must still compare as the rate it inherits, or every inheriting
   * ring would read as a distinct "no premium".
   */
  buyersPremium: BuyersPremiumScheduleInput;
  /**
   * Whether this ring holds at least one lot a buyer can see.
   *
   * The caller owns the visibility predicate, because it differs by surface:
   * the bidder asks the database (`readied_at IS NOT NULL` and not withdrawn /
   * soft-deleted), the printed guide already has the ring's lots in hand. What
   * must NOT differ is that both ask the question at all.
   */
  hasVisibleLots: boolean;
}

/**
 * How a surface should disclose the auction's buyer's premium.
 *
 * `uniform` carries the ONE rate every visible lot pays — the single pill on
 * the website, the single terms block at the top of the printed guide.
 * `varies` carries the rings to list, in the order they were supplied.
 */
export type RingPremiumDisclosure<TRing> =
  | { kind: 'uniform'; buyersPremium: BuyersPremiumScheduleInput }
  | {
      kind: 'varies';
      /** Only the rings holding visible lots, in input order. */
      rings: Array<DisclosableRing<TRing>>;
      /**
       * Whether the disclosure needs a catch-all row for visible lots sitting
       * in no ring. Its VALUE is the auction-wide rate the caller passed in;
       * this flag is only its presence, because "those lots carry no premium"
       * and "there are no unringed lots" are different facts that a null rate
       * would conflate (BL-1141 AC 7).
       */
      includesUnringedLots: boolean;
    };

/**
 * The buyer-facing identity of a rate, used to decide whether two rings charge
 * the SAME thing.
 *
 * Deliberately what a buyer READS, not a structural comparison of the stored
 * JSON. Two schedules that describe identically are indistinguishable to a
 * buyer, so treating them as distinct puts two visually identical rows in a
 * table headed "varies by ring" — the defect BL-1141 removed. A waived rate and
 * an absent one collapse to one bucket for the same reason: both read as "no
 * premium". The waiver test is `hasBuyersPremium`, the SAME predicate the
 * surfaces branch on when they print "No buyer's premium", so the bucket and
 * the rendered row cannot disagree — and note it is false only for a GENUINE
 * waiver: a 0% rate carrying a `minFee` still bills the floor and keeps its own
 * key (ADR 032 §8c).
 *
 * The key is the FULL band list, not the one-line summary. `formatBuyersPremium`
 * truncates a long schedule to `"…+N tiers"`, so keying on it would collapse two
 * genuinely different tiered schedules that happen to agree in their first few
 * bands — a buyer would be told the auction charges one rate when two are live.
 *
 * @param bp - A rate already resolved through the cascade.
 * @returns An opaque string; equal keys mean "a buyer cannot tell these apart".
 * @sideEffect None — pure.
 */
function payableRateKey(bp: BuyersPremiumScheduleInput): string {
  return buyersPremiumTermsKey(bp);
}

/**
 * Decide how a surface discloses the auction's buyer's premium (BUG-1347,
 * BL-1141 AC 1 / AC 4 / AC 5 / AC 6 / AC 7).
 *
 * Measured over the set of rates a buyer could ACTUALLY be billed: every ring
 * that holds a visible lot, plus the auction-wide rate when any visible lot
 * sits in no ring. Two or more distinct rates → list the rings. Exactly one →
 * state that one rate.
 *
 * Rings holding no visible lot are excluded from BOTH halves — they get no row,
 * and their rate does not vote. A ring nobody can bid in cannot bill anybody.
 *
 * The uniform arm returns its own resolved value rather than letting the caller
 * fall back to the auction rung: an auction defaulting to 18% whose every
 * lotted ring overrides to 10% would otherwise disclose 18% — a rate no lot
 * pays, which is a worse disclosure than the table it replaced.
 *
 * **Ring numbering is the caller's job, and must be done BEFORE filtering.** A
 * ring's position is its only identity (BL-1275), and the lot grid / printed
 * catalog number rings from the full set — so dropping the empty Ring 2 must
 * leave Ring 3 called "Ring 3". Callers assign labels over every ring, then
 * pass the whole set here with `hasVisibleLots` set; this function preserves
 * input order and never renumbers.
 *
 * @param input.rings - EVERY non-deleted ring on the auction, in display order,
 *   each with its resolved rate and whether it holds a visible lot.
 * @param input.auctionWide - The already-resolved `auction → house` rate that
 *   unringed lots pay, and the fallback when no ring can bill anyone.
 * @param input.hasUnringedVisibleLot - Whether any visible lot is in no ring.
 * @returns The disclosure plan — see {@link RingPremiumDisclosure}.
 * @sideEffect None — pure.
 */
export function decideRingPremiumDisclosure<TRing>(input: {
  rings: ReadonlyArray<DisclosableRing<TRing>>;
  auctionWide: BuyersPremiumScheduleInput;
  hasUnringedVisibleLot: boolean;
}): RingPremiumDisclosure<TRing> {
  const { rings, auctionWide, hasUnringedVisibleLot } = input;

  const disclosable = rings.filter((entry) => entry.hasVisibleLots);

  // The auction rung is payable when unringed visible lots exist — and also
  // when NO ring can bill anyone, which covers both an auction with no rings at
  // all and one whose every ring is empty. Without the second arm those cases
  // would produce an empty payable set and disclose nothing on an auction that
  // does charge a premium.
  const catchAllApplies = hasUnringedVisibleLot || disclosable.length === 0;

  const payable: BuyersPremiumScheduleInput[] = [
    ...disclosable.map((entry) => entry.buyersPremium),
    ...(catchAllApplies ? [auctionWide] : []),
  ];

  const distinct = new Set(payable.map(payableRateKey));

  if (distinct.size < 2) {
    // `payable` is non-empty whenever a ring is disclosable or the catch-all
    // applies, and one of those always holds — but fall back to the auction
    // rung rather than assuming it.
    return { kind: 'uniform', buyersPremium: payable[0] ?? auctionWide };
  }

  return {
    kind: 'varies',
    rings: disclosable,
    includesUnringedLots: catchAllApplies,
  };
}
